import { DayOfWeek } from "../../instructor-preferences/contracts/DayOfWeek";
import { OrganizationVocabulary } from "../../organization/models/Organization";
import { PortalMeResponse } from "../contracts/PortalMeResponse";
import { PortalMemberSummaryResponse } from "../contracts/PortalMemberSummaryResponse";
import { PortalOrganizationResponse } from "../contracts/PortalOrganizationResponse";
import { PortalMe, PortalMemberSummary, PortalOrganization } from "../models/PortalMe";

const FALLBACK_LANGUAGE = "en";

export class PortalMeResponseMapper {
  /**
   * Converts GET /portal/me to the UI model. A response from an older API
   * without the organization or languages falls back to an unnamed
   * organization, English, an empty vocabulary (the Default preset applies)
   * and a Monday week.
   */
  toPortalMe = (resp: PortalMeResponse): PortalMe => {
    const organization = this.toOrganization(resp.organization);
    return {
      self: this.toMember(resp.self, organization.languageCode),
      wards: (resp.wards ?? []).map((ward) => this.toMember(ward, organization.languageCode)),
      features: resp.features,
      organization,
    };
  };

  private toMember = (
    resp: PortalMemberSummaryResponse,
    organizationLanguage: string
  ): PortalMemberSummary => ({
    memberId: resp.memberId,
    firstName: resp.firstName,
    lastName: resp.lastName,
    isSelf: resp.isSelf,
    languageCode: resp.languageCode || organizationLanguage,
  });

  private toOrganization = (
    resp: PortalOrganizationResponse | null | undefined
  ): PortalOrganization => ({
    name: resp?.name ?? "",
    languageCode: resp?.languageCode || FALLBACK_LANGUAGE,
    vocabulary: this.toVocabulary(resp?.vocabulary),
    weekStart: resp?.weekStart ?? DayOfWeek.Monday,
  });

  private toVocabulary = (
    vocabulary: PortalOrganizationResponse["vocabulary"] | null | undefined
  ): OrganizationVocabulary => {
    const result: OrganizationVocabulary = {};
    for (const [locale, slots] of Object.entries(vocabulary ?? {})) {
      result[locale] = {};
      for (const [slot, term] of Object.entries(slots ?? {})) {
        result[locale][slot] = { one: term.one, other: term.other, isOverridden: term.isOverridden };
      }
    }
    return result;
  };
}
